import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Lock, Mail, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Brand } from "@/components/Brand";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { homePath } from "@/lib/roles";

function ChangePassword({ onDone }) {
  const [cur, setCur] = useState("");
  const [next, setNext] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: cur, new_password: next });
      toast.success("Password updated");
      onDone();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={submit} className="space-y-4" data-testid="change-password-form">
      <div className="space-y-1.5">
        <Label>Current (temporary) password</Label>
        <Input type="password" value={cur} onChange={(e) => setCur(e.target.value)} required data-testid="current-password-input" />
      </div>
      <div className="space-y-1.5">
        <Label>New password</Label>
        <Input type="password" value={next} onChange={(e) => setNext(e.target.value)} required data-testid="new-password-input" />
      </div>
      <Button type="submit" disabled={busy} className="w-full rounded-full bg-[#00334a] font-bold hover:bg-[#00334a]" data-testid="submit-change-password">
        {busy ? "Saving…" : "Update password"}
      </Button>
    </form>
  );
}

export default function Login() {
  const { login, refresh } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [mustChange, setMustChange] = useState(false);
  const [pendingUser, setPendingUser] = useState(null);

  const go = (u) => navigate(homePath(u));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const user = await login(email.trim().toLowerCase(), password);
      if (user.must_change_password) {
        setPendingUser(user);
        setMustChange(true);
      } else {
        toast.success(`Welcome back, ${user.name}`);
        go(user);
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid min-h-screen lg:grid-cols-2">
      <div className="relative hidden overflow-hidden p-12 lg:flex lg:flex-col lg:justify-between">
        <img src="/brand/IMG_9503.jpeg" alt="Swim instructor floating a child" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-[#00334a]/78" />
        <div className="relative z-10"><Brand light /></div>
        <div className="relative z-10">
          <h2 className="font-heading text-4xl font-semibold leading-tight text-white">
            Every gift is a<br />child who floats.
          </h2>
          <p className="mt-4 max-w-sm text-white/85">
            Sign in to manage campaigns, rally your team, and watch the leaderboard rise in real time.
          </p>
        </div>
        <div className="relative z-10 text-xs text-white/70">LSSP × Hope Floats Foundation · Private portal</div>
      </div>

      <div className="flex items-center justify-center bg-[#f8f3e9] p-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="w-full max-w-sm"
        >
          <Link to="/" className="mb-8 inline-flex items-center gap-1.5 text-sm font-semibold text-[#3f6670] hover:text-[#c97049]">
            <ArrowLeft className="h-4 w-4" /> Back to home
          </Link>
          <div className="lg:hidden"><Brand /></div>
          <h1 className="mt-6 font-heading text-3xl font-extrabold text-[#00334a]">Welcome back</h1>
          <p className="mt-1 text-sm text-[#7c7a6a]">Staff and supporters sign in here.</p>

          <form onSubmit={submit} className="mt-8 space-y-4" data-testid="login-form">
            <div className="space-y-1.5">
              <Label htmlFor="email">Email</Label>
              <div className="relative">
                <Mail className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#a9a795]" />
                <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required placeholder="you@littlesplashswimpro.com" className="pl-9" data-testid="login-email-input" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password">Password</Label>
              <div className="relative">
                <Lock className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#a9a795]" />
                <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required className="pl-9" data-testid="login-password-input" />
              </div>
            </div>
            <Button type="submit" disabled={busy} className="w-full rounded-full hf-btn-coral py-6 text-base font-bold" data-testid="login-submit-button">
              {busy ? "Signing in…" : "Sign in"}
            </Button>
          </form>
          <p className="mt-6 text-center text-xs text-[#7c7a6a]">
            Supporters join by invitation only. Ask staff for your invite link.
          </p>
        </motion.div>
      </div>

      <Dialog open={mustChange} onOpenChange={() => {}}>
        <DialogContent className="sm:max-w-md" data-testid="must-change-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Set a new password</DialogTitle>
            <DialogDescription>
              For security, please replace your temporary password before continuing.
            </DialogDescription>
          </DialogHeader>
          <ChangePassword onDone={async () => { await refresh(); go(pendingUser); }} />
        </DialogContent>
      </Dialog>
    </div>
  );
}
